/**
 * Hook for chart wheel/scroll interaction handling.
 * Encapsulates zoom and horizontal pan via scroll gestures.
 */

import { useCallback, useEffect, useRef, type RefObject } from "react";

import {
  calculateZoomFactor,
  detectGestureType,
  shouldPan,
  shouldZoom,
} from "~utils/ticker-chart";
import type { ChartStore } from "~types/types";

/** Delay before snapping viewport to data points after interaction ends */
const SNAP_DEBOUNCE_MS = 150;

export interface UseWheelInteractionOptions {
  /** Ref to the SVG element to attach wheel listener */
  elementRef: RefObject<SVGElement | null>;
  /** Chart store for zoom/pan operations */
  store: ChartStore;
  /** Enable zoom via wheel/pinch */
  zoomEnabled: boolean;
  /** Enable pan via horizontal scroll */
  panEnabled: boolean;
}

/**
 * Hook that attaches wheel event listener for zoom and pan.
 * Handles:
 * - Mouse wheel: zoom in/out
 * - Trackpad pinch: zoom in/out
 * - Trackpad horizontal scroll: pan left/right
 *
 * Snaps viewport to data points after interaction ends (debounced).
 */
export function useWheelInteraction({
  elementRef,
  store,
  zoomEnabled,
  panEnabled,
}: UseWheelInteractionOptions): void {
  const snapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const storeRef = useRef(store);

  // Keep store ref in sync
  storeRef.current = store;

  // Debounced snap function - snaps viewport to nearest inner data points
  const scheduleSnap = useCallback(() => {
    // Clear any pending snap
    if (snapTimeoutRef.current) {
      clearTimeout(snapTimeoutRef.current);
    }

    // Schedule snap after user stops interacting
    snapTimeoutRef.current = setTimeout(() => {
      storeRef.current.snapViewport();
      snapTimeoutRef.current = null;
    }, SNAP_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    const el = elementRef.current;

    if (!el) return undefined;

    const handleWheel = (event: WheelEvent) => {
      const rect = el.getBoundingClientRect();
      const layout = storeRef.current.getLayout();
      const chartX = event.clientX - rect.left + layout.priceArea.x;

      const gesture = detectGestureType(
        event.deltaX,
        event.deltaY,
        event.ctrlKey
      );

      const willZoom = zoomEnabled && shouldZoom(gesture);
      const willPan = panEnabled && shouldPan(gesture);

      // Only handle recognized gestures - let unrecognized ones propagate
      if (!willZoom && !willPan) {
        return;
      }

      // Prevent default for handled gestures
      event.preventDefault();
      event.stopPropagation();

      // Hide crosshair during zoom/pan interactions
      storeRef.current.clearHover();

      // Handle zoom (pinch gesture or mouse wheel)
      if (willZoom) {
        const zoomFactor = calculateZoomFactor(event.deltaY);

        storeRef.current.zoom(zoomFactor, chartX);
      }

      // Handle horizontal scroll (pan) - use panWithoutSnap for smooth interaction
      if (willPan) {
        storeRef.current.panWithoutSnap(event.deltaX);
      }

      // Schedule snap after interaction ends
      scheduleSnap();
    };

    el.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      el.removeEventListener("wheel", handleWheel);

      // Cleanup pending snap on unmount
      if (snapTimeoutRef.current) {
        clearTimeout(snapTimeoutRef.current);
      }
    };
  }, [elementRef, zoomEnabled, panEnabled, scheduleSnap]);
}
